import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { ContextKey } from '@pubstack/common/src/contextKey'
import { GamConfig } from '@pubstack/common/src/gam/gamConfig'
import { integerRegExp } from '@pubstack/common/src/input'
import { Site } from '@pubstack/common/src/site'
import { AdmSiteConfig } from '@pubstack/common/src/siteConfig'
import { FunctionComponent, useEffect, useState } from 'react'
import { FieldErrors, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import Button from '~/components/Button'
import { ContentCard } from '~/components/ContentCard'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { Input } from '~/components/Input'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { PageContent } from '~/components/PageContent'
import { Skeleton } from '~/components/Skeleton'
import { Status } from '~/components/Status'
import Table, { handleTableSearchAndSort, onColumnSort, TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import { TableCollapsibleRow, TableCollapsibleRowChevronColumn } from '~/components/table/TableCollapsibleRow'
import { Tooltip } from '~/components/Tooltip'
import { useScopePermissions } from '~/auth/useScopePermissions'
import { ModuleHeader } from '~/feature-components/ModuleHeader'
import { WithClassName } from '~/types/utils'
import { PureAdmGamIntegrationHelpFlyout } from './PureAdmGamIntegrationHelpFlyout'
import { PureAdmGamSiteConfigForm } from './PureAdmGamSiteConfigForm'

const Container = styled.div<{ fromSiteId: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  ${({ fromSiteId }) =>
    !fromSiteId &&
    css`
          padding: 1.25rem 0;
        `};
  
`

const NetworkIdForm = styled.form`
  display: inline-flex;
  align-items: center;
  gap: 16px;
`

const SiteGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const SiteTitle = styled(TableCell)`
  font-weight: bold;
`
const FilterAction = styled.form`
  display: flex;
`
const BackButton = styled.div`
  display: flex;
  justify-content: center;
`

type PureAdmGamIntegrationPageProps = WithClassName & {
  breadcrumbs: React.ReactNode
  isLoading: boolean
  gamConfig: GamConfig
  contextKeys: ContextKey[]
  createNetworkId: (networkId: string) => Promise<void>
  createSiteConfig: (siteConfig: AdmSiteConfig) => Promise<void>
  onNavigateToContextKeys: () => void
  onNavigateToAdUnits: () => void
  onBackClick: () => void
  baseCDNUrl: string
  fromSiteId?: string
}

const UnstyledPureAdmGamIntegrationPage: FunctionComponent<PureAdmGamIntegrationPageProps> = ({
  className,
  breadcrumbs,
  isLoading,
  gamConfig,
  createNetworkId,
  createSiteConfig,
  onNavigateToContextKeys,
  onNavigateToAdUnits,
  onBackClick,
  contextKeys,
  baseCDNUrl,
  fromSiteId,
}) => {
  const flyout = useGlobalFlyout()
  // The network id is what every GAM call on the scope is made against, so it is the owner's to set. The
  // control stays on screen and says why: seeing that the setting exists is half of knowing to ask.
  const { canEdit, disabledReason } = useScopePermissions()
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [columns, setColumns] = useState<TableColumns<Site>>([
    {
      name: 'Site',
      isSortable: true,
      order: 'ascending',
      attributeSort: 'name',
    },
    {
      name: 'Status',
      isSortable: false,
    },
    TableCollapsibleRowChevronColumn,
  ])

  const { control: searchControl } = useForm<{ search: string }>({
    defaultValues: {
      search: fromSiteId || '',
    },
  })

  const search = useWatch({ control: searchControl, name: 'search' })

  const [isOpen, setIsOpen] = useState<{ [key: string]: boolean }>(fromSiteId ? { [fromSiteId]: true } : {})
  const [previousFromSiteId, setPreviousFromSiteId] = useState(fromSiteId)

  // the site to expand comes from the url: realign on change, during render rather than in an effect
  if (previousFromSiteId !== fromSiteId) {
    setPreviousFromSiteId(fromSiteId)
    if (fromSiteId) {
      setIsOpen({ [fromSiteId]: true })
    }
  }
  const {
    handleSubmit,
    formState: { errors, isDirty },
    control,
    reset,
  } = useForm<GamConfig>({
    defaultValues: gamConfig,
  })

  const onNetworkIdEdit = () => {
    setIsEditing(!isEditing)
  }
  const onNetworkIdSubmit: SubmitHandler<GamConfig> = (formValue) => {
    createNetworkId(formValue.networkId)
    setIsEditing(!isEditing)
  }

  const getErrorMessage = (errors: FieldErrors<GamConfig>) => {
    if (errors.networkId?.type === 'required') {
      return 'NetworkId is required'
    }
    if (errors.networkId?.type === 'pattern') {
      return 'The id should be numeric'
    }
  }
  const sortedSites = handleTableSearchAndSort(columns, gamConfig.sites, search, ['name', 'id'])

  useEffect(() => {
    reset({
      ...gamConfig,
    })
  }, [gamConfig])

  const container = (
    <Container fromSiteId={!!fromSiteId}>
      <ModuleHeader
        catalogItemCode={'gam'}
        catalogItemType={'adServer'}
        isLoading={isLoading}
        status={<Status state={gamConfig.networkId ? 'active' : 'inactive'}>{gamConfig.networkId ? 'Active' : 'Not used'}</Status>}
        baseCDNUrl={baseCDNUrl}
      />
      <ContentCard className={className} color={Colors.Violet}>
        <NetworkIdForm id={'gamNetworkIdForm'} onSubmit={handleSubmit(onNetworkIdSubmit)}>
          <span>Network Id:</span>
          <Input disabled={!isEditing} type={'text'} name={'networkId'} rules={{ required: true, pattern: integerRegExp }} control={control} error={getErrorMessage(errors)} />
          {!isEditing ? (
            <Tooltip title={disabledReason} disabled={canEdit}>
              <Button disabled={!canEdit} variant={'primary'} onClick={onNetworkIdEdit} trackingLabel={'adm-integrations-gam-network-id-edit'}>
                Edit
              </Button>
            </Tooltip>
          ) : (
            <Button disabled={!isDirty} variant={'primary'} onClick={handleSubmit(onNetworkIdSubmit)} trackingLabel={'adm-integrations-gam-network-id-save'}>
              Save
            </Button>
          )}
        </NetworkIdForm>
      </ContentCard>
      {!fromSiteId && (
        <FilterAction>
          <Input name={'search'} type={'text'} iconLeft={'search'} labelIsPlaceholder label={'Search'} control={searchControl} />
        </FilterAction>
      )}
      <SiteGroup>
        {!isLoading ? (
          gamConfig.networkId !== '0' && gamConfig.sites.length > 0 ? (
            <Table columns={columns} isLoading={isLoading} onClickHeading={(column) => onColumnSort(columns, column, setColumns)}>
              {sortedSites.map((site) => {
                const siteConfig = gamConfig.siteConfigs.find((s) => site.id === s.siteId)
                const toggleCollapse = () => {
                  setIsOpen((previousState) => ({ ...previousState, [site.id]: !previousState[site.id] }))
                }
                return (
                  <TableCollapsibleRow
                    key={site?.id}
                    isOpen={isOpen[site.id] ?? false}
                    onCollapse={{
                      onClick: toggleCollapse,
                      trackingLabel: 'adm-gam-settings-collapse-apply',
                    }}
                    content={() => (
                      <PureAdmGamSiteConfigForm
                        contextKeys={contextKeys}
                        siteConfig={siteConfig}
                        createSiteConfig={(data) => {
                          createSiteConfig({ ...data, siteId: data.siteId || site.id, scopeId: data.scopeId || gamConfig.scopeId })
                          toggleCollapse()
                        }}
                      />
                    )}
                  >
                    <SiteTitle>{site.name}</SiteTitle>
                    <TableCell>
                      <Status state={siteConfig?.adUnitPathConfig ? 'active' : 'inactive'}>{siteConfig?.adUnitPathConfig ? 'Configured' : 'Not configured'}</Status>
                    </TableCell>
                  </TableCollapsibleRow>
                )
              })}
            </Table>
          ) : (
            <IllustrationMessage iconName={'night_sky'} iconSize={'190px'} title={'You don’t have any sites yet or your network Id is not set.'} />
          )
        ) : (
          <Skeleton bigger width={'100%'} />
        )}
      </SiteGroup>
      {fromSiteId && (
        <BackButton>
          <Button variant={'primary'} onClick={onBackClick} trackingLabel={'adm-integrations-gam-back'}>
            Back to site
          </Button>
        </BackButton>
      )}
    </Container>
  )

  return fromSiteId ? (
    <PageContent
      className={className}
      breadcrumbs={breadcrumbs}
      headerActions={
        <>
          <Button variant={'tertiary'} onClick={onBackClick} trackingLabel={'adm-forms-back'}>
            Back
          </Button>

          <Button
            variant={'tertiary'}
            onClick={() =>
              flyout.open(
                PureAdmGamIntegrationHelpFlyout,
                { color: Colors.Violet, closeTrackingLabel: 'adm-integrations-gam-help-close' },
                {
                  navigateToAdUnits: () => {
                    flyout.close()
                    onNavigateToAdUnits()
                  },
                  navigateToContextKeys: () => {
                    flyout.close()
                    onNavigateToContextKeys()
                  },
                }
              )
            }
            iconName={'help'}
            trackingLabel={'adm-integrations-gam-help'}
          >
            Help
          </Button>
        </>
      }
    >
      {container}
    </PageContent>
  ) : (
    container
  )
}

export const PureAdmGamIntegrationPage = styled(UnstyledPureAdmGamIntegrationPage)``
